import { inject, Injectable } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';

import { getWorkflowNodeBase } from '@blaze-runtime/workflow-domain';
import { ComparisonOperator, LogicalType, QueryNodeType } from '@blaze-shared/api-contracts-domain';
import {
  type DataBindingSourceInfo,
  type FormGroupActionType,
  updateFormAssignmentsFromColumns,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { normalizeVarName } from '@blaze-shared/variables-util';
import {
  type AggregateWorkflowNode,
  type AssignWorkflowNode,
  getAutoDisplayInfo,
  getInput1DisplayInfo,
  type InsertWorkflowNode,
  type UpdateWorkflowNode,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { FormGroupUtilsService } from '../form-group-utils.service';

import { formGroupDataSourceVariable } from './form-group-naming';

/**
 * The nodes that write one form's dataset, and publish the row id that write produced.
 *
 * A form group's other forms reference that id, so every write is followed by an assignment
 * naming it — which is what lets the group span several datasets at all.
 */
@Injectable({ providedIn: 'root' })
export class FormWriteNodesService {
  private readonly formGroupUtilsService = inject(FormGroupUtilsService);
  private readonly schemaService = inject(SchemaService);

  /**
   * The node that writes one form's row: an insert when the group adds, an update when it edits.
   *
   * Editing is always an upsert, because a form group may be editing a row that does not exist on
   * every dataset it spans.
   *
   * @returns nothing when the form is not configured enough to write anything, and for a `link`
   * action on the main dataset with nothing to link — there is no row of its own to write.
   */
  getFormGroupActionNode(
    action: FormGroupActionType | undefined,
    elementDataSource: DataBindingSourceInfo | undefined,
    isMain: boolean,
    existingActionNode: WorkflowNode | undefined,
    formName: string | undefined,
    columnFormNameMapping: Record<string, string>,
    columnValueSubstitutions: Partial<Record<string, { value: string; label: string }>>,
    orphanedValues: RegExp,
    idColumn: string | undefined,
    successMessage: string | undefined,
  ): WorkflowNode | undefined {
    if (!action || !elementDataSource || !formName || !idColumn) {
      return undefined;
    }
    const datasetId = this.schemaService.getDatasetId(
      elementDataSource.id,
      elementDataSource.sourceType,
    );

    switch (action) {
      case 'add':
        return {
          ...getWorkflowNodeBase(
            'Insert',
            this.formGroupUtilsService.workflowNodeId('upsert', elementDataSource),
          ),
          ...existingActionNode,
          nodeType: 'Insert',
          datasetId,
          successMessage,
          assignments: updateFormAssignmentsFromColumns(
            existingActionNode,
            elementDataSource.columns,
            formName,
            columnFormNameMapping,
            columnValueSubstitutions,
            orphanedValues,
          ),
        } as InsertWorkflowNode;

      case 'edit':
      case 'link':
        return action === 'link' && isMain && isEmpty(columnValueSubstitutions)
          ? undefined
          : ({
              ...getWorkflowNodeBase(
                'Update',
                this.formGroupUtilsService.workflowNodeId('upsert', elementDataSource),
              ),
              upsert: true,
              ...existingActionNode,
              nodeType: 'Update',
              currentRow: false,
              datasetId,
              successMessage,
              assignments: updateFormAssignmentsFromColumns(
                existingActionNode,
                elementDataSource.columns,
                formName,
                columnFormNameMapping,
                columnValueSubstitutions,
                orphanedValues,
                action === 'link' && isMain && !isEmpty(columnValueSubstitutions),
              ),
              queryConditions: [],
              query: {
                nodeType: QueryNodeType.Logical,
                logicalType: LogicalType.And,
                children: [
                  {
                    nodeType: QueryNodeType.Comparison,
                    column: idColumn,
                    operator: ComparisonOperator.Eq,
                    value: `$$${formName}["${idColumn}"]`,
                    isOptional: action === 'edit' || !isMain,
                    isFormulaValue: false,
                    displayInfos: [getAutoDisplayInfo(formName, idColumn, true)],
                  },
                ],
              },
            } as UpdateWorkflowNode);

      default:
        return undefined;
    }
  }

  /**
   * What follows every form's write: the id it produced, assigned to a variable the other forms
   * reference. A form collecting many rows aggregates their ids into one list first.
   */
  getFormGroupCommonActions(dataSource: DataBindingSourceInfo, multiple: boolean): WorkflowNode[] {
    const idColumn = this.schemaService.getDataSourceIdColumn(dataSource.id, dataSource.sourceType);
    return [
      ...(multiple
        ? [
            <AggregateWorkflowNode>{
              ...getWorkflowNodeBase(
                'Aggregate',
                this.formGroupUtilsService.workflowNodeId('singularize', dataSource),
              ),
              functionDefs: [{ name: 'ARRAY', column: idColumn, alias: 'ids' }],
              groupByColumns: [],
            },
          ]
        : []),
      <AssignWorkflowNode>{
        id: this.formGroupUtilsService.workflowNodeId('assign', dataSource),
        nodeType: 'Assign',
        outputName: normalizeVarName(formGroupDataSourceVariable(dataSource)),
        isFormulaValue: false,
        scope: 'Workflow',
        value: multiple ? `$$input1.ids` : `$$input1.${idColumn}`,
        displayInfos: [getInput1DisplayInfo(multiple ? 'ids' : idColumn)],
      },
    ];
  }
}
